import React, { useEffect, useMemo, useRef } from "react";

import { mdiAutoFix, mdiUploadNetworkOutline } from "@mdi/js";
import Icon from "@mdi/react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { CUEPOINT_TIMECODE_REGEX } from "../../../constants/media";
import {
  blocksCuepoint,
  CUEPOINT_PRESET_MINUTES,
  maskCuepoint,
  minutesToOffsetTimecode,
  NO_CUEPOINT_PRESET,
  valuesMatchPreset,
} from "../../../utils/cuepoints";
import prettyDurations, {
  DURATION_UNITS,
  timecodeToDuration,
} from "../../../utils/durations";
import { BUTTON_VARIANTS } from "../../button/Button";
import Input from "../../form/Input";
import Select from "../../form/Select";
import Modal from "../../modal/Modal";

import type { JSX } from "react";

import type { MediaCuepointAdded } from "../../../api/MediaAPI.types";
import type { ModalActions } from "../../modal/Modal";

function ModalHeader(): JSX.Element {
  const { t } = useTranslation("editor-content");

  return (
    <div className="flex flex-wrap items-center gap-[0.9375rem]">
      <div className="border-border flex rounded-[0.46875rem] border p-[0.46875rem]">
        <Icon path={mdiUploadNetworkOutline} rotate={90} size={1.25} />
      </div>
      <div className="text-[1.171875rem] font-semibold">
        {t("generateAutoCuepoints")}
      </div>
    </div>
  );
}

interface PresetOption {
  value: string;
  label: string;
}

const DEFAULT_START_OFFSET = "00:00:00:00";

interface FormValues {
  preset: PresetOption;
  startOffset: string;
  numberOfCuepoints?: number;
  minutesBetweenCuepoints?: number;
}

interface AutoCuePointsModalProps {
  isOpen: boolean;
  onClose: () => void;
  mediaDurationMs: number | null;
  addCuepointList: (cuepointList: MediaCuepointAdded[]) => void;
}

function AutoCuePointsModal({
  isOpen,
  onClose,
  mediaDurationMs,
  addCuepointList,
}: AutoCuePointsModalProps): JSX.Element {
  const { t } = useTranslation("editor-content");

  const NO_PRESET_OPTION = useMemo<PresetOption>(
    () => ({
      value: NO_CUEPOINT_PRESET,
      label: t("noPreset"),
    }),
    [t],
  );

  const presetOptionList = useMemo<PresetOption[]>(
    () => [
      NO_PRESET_OPTION,
      ...CUEPOINT_PRESET_MINUTES.map((minutes) => ({
        value: String(minutes),
        label: `${minutes} ${t("editor-utils:minuteUnit")}`,
      })),
    ],
    [NO_PRESET_OPTION, t],
  );

  const methods = useForm<FormValues>({
    values: {
      preset: NO_PRESET_OPTION,
      startOffset: DEFAULT_START_OFFSET,
      numberOfCuepoints: undefined,
      minutesBetweenCuepoints: undefined,
    },
  });

  // Guards re-entrant watch events triggered by our own setValue calls, so the
  // forward-fill, the clear-on-"no preset", and the reverse reset never trigger
  // one another (relies on react-hook-form notifying watch subscribers
  // synchronously inside setValue).
  const isSyncingRef = useRef(false);

  useEffect(() => {
    const subscription = methods.watch((formValues, { name }) => {
      if (isSyncingRef.current) {
        return;
      }

      const selectedPreset = formValues.preset;
      const presetMinutes = Number(selectedPreset?.value);
      const isRealPreset =
        selectedPreset != null &&
        selectedPreset.value !== NO_CUEPOINT_PRESET &&
        !Number.isNaN(presetMinutes);

      // Preset chosen from the dropdown: fill the two fields from a real preset,
      // or clear them back to their defaults when "no preset" is picked.
      if (name === "preset") {
        isSyncingRef.current = true;
        if (isRealPreset) {
          methods.setValue(
            "startOffset",
            minutesToOffsetTimecode(presetMinutes),
            { shouldValidate: true },
          );
          methods.setValue("minutesBetweenCuepoints", presetMinutes, {
            shouldValidate: true,
          });
        } else {
          methods.setValue("startOffset", DEFAULT_START_OFFSET, {
            shouldValidate: false,
          });
          methods.setValue("minutesBetweenCuepoints", undefined, {
            shouldValidate: false,
          });
        }
        isSyncingRef.current = false;
        return;
      }

      // User edited a field away from the active preset: drop the preset label
      // without touching the value they just typed.
      if (
        (name === "startOffset" || name === "minutesBetweenCuepoints") &&
        isRealPreset &&
        !valuesMatchPreset(
          presetMinutes,
          formValues.startOffset ?? "",
          formValues.minutesBetweenCuepoints,
        )
      ) {
        isSyncingRef.current = true;
        methods.setValue("preset", NO_PRESET_OPTION);
        isSyncingRef.current = false;
      }
    });

    return () => subscription.unsubscribe();
  }, [methods, t, NO_PRESET_OPTION]);

  const mediaDurationFormated = prettyDurations(mediaDurationMs ?? 0, {
    valueUnit: DURATION_UNITS.MILLISECONDS,
  }).timecodeDuration;

  const generateCuepoints = (formData: FormValues): MediaCuepointAdded[] => {
    const { startOffset, numberOfCuepoints, minutesBetweenCuepoints } =
      formData;

    if (!minutesBetweenCuepoints || !startOffset || !mediaDurationMs) {
      return [];
    }

    const startOffsetMs =
      timecodeToDuration(startOffset).millisecondsWithFrames;
    const intervalMs = minutesBetweenCuepoints * 60 * 1000;

    const maxPossibleCuepointsByMediaDuration =
      Math.floor((mediaDurationMs - startOffsetMs) / intervalMs) + 1;

    const maxCuepoints = numberOfCuepoints
      ? Math.min(numberOfCuepoints, maxPossibleCuepointsByMediaDuration)
      : maxPossibleCuepointsByMediaDuration;

    const generatedCuepoints = Array.from(
      { length: maxCuepoints },
      (_, index) => {
        const currentTimeMs = startOffsetMs + index * intervalMs;

        const currentTimecode = prettyDurations(currentTimeMs, {
          valueUnit: DURATION_UNITS.MILLISECONDS,
        }).timecodeDuration;

        return {
          enabled: true,
          timecode: currentTimecode,
        };
      },
    );

    return generatedCuepoints;
  };

  const handleClose = (): void => {
    onClose();
    methods.reset();
  };

  const handleSubmit = (data: FormValues): void => {
    const generatedCuepoints = generateCuepoints(data);
    addCuepointList(generatedCuepoints);
    handleClose();
  };

  const validateTimecode = (value: string): true | string => {
    if (!CUEPOINT_TIMECODE_REGEX.test(value)) {
      return t("invalidTimecodeFormat");
    }

    const currentTimecodeMs = timecodeToDuration(value).millisecondsWithFrames;

    if (mediaDurationMs && currentTimecodeMs > mediaDurationMs) {
      return t("durationExceedsMediaDuration", {
        mediaDuration: mediaDurationFormated,
      });
    }
    return true;
  };

  const labelButton = (
    <div className="flex items-center justify-center gap-[0.46875rem]">
      <Icon path={mdiAutoFix} size={0.9375} />
      {t("generateCuepoints")}
    </div>
  );

  const formId = "autoCuePointsForm";

  const confirmModalActions: ModalActions[] = [
    {
      label: labelButton,
      buttonVariant: BUTTON_VARIANTS.PRIMARY,
      isFullWidth: true,
      isCloseAfterAction: false,
      formId,
    },
  ];

  return (
    <Modal
      isDivided
      actions={confirmModalActions}
      header={<ModalHeader />}
      isOpen={isOpen}
      onClose={handleClose}
    >
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      <FormProvider {...methods}>
        <form
          className="flex flex-col gap-[1.5625rem]"
          id={formId}
          onSubmit={methods.handleSubmit(handleSubmit)}
        >
          <Select
            isCompact
            isPortal
            label={t("preset")}
            name="preset"
            options={presetOptionList}
          />
          <Input
            isCompact
            isRequired
            blocks={blocksCuepoint}
            label={t("startOffset")}
            mask={maskCuepoint}
            name="startOffset"
            validate={validateTimecode}
          />
          <Input
            isCompact
            isRequired
            label={t("minutesBetweenCuepoints")}
            min={1}
            name="minutesBetweenCuepoints"
            type="number"
          />
          <Input
            isCompact
            label={t("maximumNumberOfCuepoints")}
            min={1}
            name="numberOfCuepoints"
            type="number"
          />
        </form>
      </FormProvider>
    </Modal>
  );
}

export default AutoCuePointsModal;
